import { getLocationsStatsRetrieveQueryOptions } from "@sidegigllc/api/client";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { useFeatureFlag } from "providers/LaunchDarkly";
import { useActiveLocationId } from "services/locationOrgContext";
import { askQwickTooltipSeenAtom } from "../lib/takeover";
import { useOnboarding } from "./useOnboarding";
import { useOnTakeover } from "./useOnTakeover";

/**
 * The Ask Qwick button under agent-web-onboarding: before the location's first post it opens the takeover on the
 * role question; otherwise it toggles the docked chat as before.
 */
export const useAskQwick = (toggleChat: () => void) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [locationId] = useActiveLocationId();
  const restyled = useFeatureFlag("agent-web-onboarding");
  const { offered } = useOnboarding(locationId);
  const setTooltipSeen = useSetAtom(askQwickTooltipSeenAtom);
  const onTakeover = useOnTakeover();

  const open = async () => {
    if (!offered || locationId === undefined) {
      toggleChat();
      return;
    }
    // On a cold load the first-post answer may still be in flight; a click waits for it rather than guessing the panel.
    const stats = await queryClient
      .query({ ...getLocationsStatsRetrieveQueryOptions(locationId), staleTime: Number.POSITIVE_INFINITY })
      .catch(() => null);
    if (stats?.has_posted_first_gig !== false) {
      toggleChat();
      return;
    }
    void navigate({ to: "/locations/$locationId/post/shift", params: { locationId: String(locationId) } });
  };

  const onClick = () => {
    if (restyled) setTooltipSeen(true);
    // Opening the takeover it is already on would send its first turn into the guided conversation again.
    if (onTakeover) return;
    void open();
  };

  return { restyled, onClick };
};
